import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Button, Row, Col, Image, Typography, Space, Skeleton, Alert } from 'antd';
import { PhoneOutlined, EnvironmentOutlined, MailOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import API_URL from '../../config/api';

const { Title, Paragraph, Text } = Typography;

interface Business {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  website?: string;
  logo?: string;
  template: string;
  category: string;
}

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  image?: string;
}

const PublicBusinessWebsite: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const [business, setBusiness] = useState<Business | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchBusinessData();
  }, [businessId]);

  const fetchBusinessData = async () => {
    setError(null);
    try {
      // Fetch business details
      const businessResponse = await fetch(`${API_URL}/business/${businessId}`);
      if (businessResponse.ok) {
        const businessData = await businessResponse.json();
        setBusiness(businessData.business);
      } else {
        setError('Business not found.');
        setBusiness(null);
        setProducts([]);
        return;
      }

      // Fetch products/services based on template
      const productsResponse = await fetch(`${API_URL}/products?business_id=${businessId}&limit=6`);
      if (productsResponse.ok) {
        const productsData = await productsResponse.json();
        setProducts(productsData.products || []);
      } else {
        setProducts([]);
      }
    } catch (error) {
      setError('Failed to fetch business data. Please try again later.');
      setBusiness(null);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', padding: 24 }}>
        <Skeleton.Image style={{ width: '100%', height: 180, marginBottom: 24 }} active />
        <Skeleton active paragraph={{ rows: 4 }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', padding: 24 }}>
        <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />
      </div>
    );
  }

  if (!business) {
    return null;
  }

  const renderTemplateContent = () => {
    switch (business.category) {
      case 'retail':
        return (
          <div>
            <Title level={2} style={{ textAlign: 'center', marginBottom: '40px' }}>
              🛍️ Our Products
            </Title>
            <Row gutter={[16, 16]}>
              {products.map(product => (
                <Col xs={24} sm={12} md={8} key={product.id}>
                  <Card
                    hoverable
                    cover={product.image ? <Image alt={product.name} src={product.image} /> : null}
                    actions={[
                      <Button type="primary" icon={<ShoppingCartOutlined />}>
                        Contact Us
                      </Button>
                    ]}
                  >
                    <Card.Meta
                      title={product.name}
                      description={
                        <div>
                          <Text strong>£{product.price}</Text>
                          <Paragraph ellipsis={{ rows: 2 }}>{product.description}</Paragraph>
                        </div>
                      }
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        );

      case 'medical':
        return (
          <div>
            <Title level={2} style={{ textAlign: 'center', marginBottom: '40px' }}>
              🏥 Our Services
            </Title>
            <Row gutter={[16, 16]}>
              {products.map(service => (
                <Col xs={24} sm={12} md={8} key={service.id}>
                  <Card hoverable>
                    <Card.Meta
                      title={service.name}
                      description={
                        <div>
                          <Text strong>£{service.price}</Text>
                          <Paragraph>{service.description}</Paragraph>
                        </div>
                      }
                    />
                    <Button type="primary" style={{ marginTop: '16px' }}>
                      Book Appointment
                    </Button>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        );

      case 'hospitality':
        return (
          <div>
            <Title level={2} style={{ textAlign: 'center', marginBottom: '40px' }}>
              🍽️ Our Menu
            </Title>
            <Row gutter={[16, 16]}>
              {products.map(item => (
                <Col xs={24} sm={12} md={8} key={item.id}>
                  <Card
                    hoverable
                    cover={item.image ? <Image alt={item.name} src={item.image} /> : null}
                  >
                    <Card.Meta
                      title={item.name}
                      description={
                        <div>
                          <Text strong>£{item.price}</Text>
                          <Paragraph>{item.description}</Paragraph>
                        </div>
                      }
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        );

      default:
        return (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Title level={3}>Welcome to {business.name}</Title>
            <Paragraph>
              We provide professional {business.category} services. Contact us to learn more about our offerings.
            </Paragraph>
          </div>
        );
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      {/* Header */}
      <div style={{ background: 'white', padding: '16px 0', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 12px' }}>
          <Row align="middle" justify="space-between" gutter={[8, 8]}>
            <Col>
              <Space>
                {business.logo && <Image src={business.logo} alt="logo" width={44} height={44} style={{ objectFit: 'contain', borderRadius: 8 }} />}
                <Title level={4} style={{ margin: 0, fontSize: 22 }}>{business.name}</Title>
              </Space>
            </Col>
            <Col>
              <Space>
                <Button type="primary" size="middle" style={{ minWidth: 110, fontWeight: 500 }}>Contact Us</Button>
                <Button size="middle" style={{ minWidth: 90 }}>Call Now</Button>
              </Space>
            </Col>
          </Row>
        </div>
      </div>

      {/* Hero Section */}
      <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', padding: '40px 0 32px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 12px', textAlign: 'center' }}>
          <Title level={2} style={{ color: 'white', marginBottom: 12, fontSize: 32, lineHeight: 1.2 }}>{`Welcome to ${business.name}`}</Title>
          <Paragraph style={{ fontSize: 16, color: 'white', maxWidth: 600, margin: '0 auto' }}>
            Professional {business.category} services tailored for your needs.
            {business.category === 'retail' && ' Discover our amazing products and shop with confidence.'}
            {business.category === 'medical' && ' Your health and wellness are our top priority.'}
            {business.category === 'hospitality' && ' Experience exceptional dining and hospitality.'}
          </Paragraph>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 8px' }}>
        {renderTemplateContent()}
      </div>

      {/* Contact Section */}
      <div style={{ background: 'white', padding: '32px 0', marginTop: 32 }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 8px' }}>
          <Title level={3} style={{ textAlign: 'center', marginBottom: 28, fontSize: 24 }}>
            📞 Contact Us
          </Title>
          <Row gutter={[20, 20]}>
            <Col xs={24} md={8}>
              <Card style={{ textAlign: 'center', borderRadius: 12, minHeight: 140 }} bodyStyle={{ padding: 18 }}>
                <PhoneOutlined style={{ fontSize: 28, color: '#1890ff', marginBottom: 10 }} />
                <Title level={5} style={{ margin: 0 }}>Phone</Title>
                <Text>{business.phone}</Text>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card style={{ textAlign: 'center', borderRadius: 12, minHeight: 140 }} bodyStyle={{ padding: 18 }}>
                <MailOutlined style={{ fontSize: 28, color: '#1890ff', marginBottom: 10 }} />
                <Title level={5} style={{ margin: 0 }}>Email</Title>
                <Text>{business.email}</Text>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card style={{ textAlign: 'center', borderRadius: 12, minHeight: 140 }} bodyStyle={{ padding: 18 }}>
                <EnvironmentOutlined style={{ fontSize: 28, color: '#1890ff', marginBottom: 10 }} />
                <Title level={5} style={{ margin: 0 }}>Address</Title>
                <Text>{business.address}</Text>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* Footer */}
      <div style={{ background: '#001529', color: 'white', padding: '16px 0', textAlign: 'center' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 8px' }}>
          <Text style={{ color: 'white', fontSize: 14 }}>
            © 2024 {business.name}. Powered by ShahoSuite
          </Text>
        </div>
      </div>
    </div>
  );
};

export default PublicBusinessWebsite;